import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { EditOptionsFooter, LIST_SIZE_OPTIONS } from './ListChipCell'
import { useResizableSize } from './ResizeCorner'

const MAX_SUGGESTIONS = 50

/**
 * The editor of an `autofill` cell: free text, with the column's options that
 * contain what was typed offered below. Unlike a smartchip, a value outside the
 * list is kept as typed.
 */
export function AutofillCellEditor({
  options,
  initialText,
  className,
  onConfirm,
  onCancel,
  onNavigate,
  onEditOptions,
}: {
  options: readonly string[]
  initialText: string
  className?: string
  onConfirm: (value: string) => void
  onCancel: () => void
  onNavigate: (rowDelta: number, colDelta: number, value: string) => void
  /** Shown only to those who may edit the column's options: opens its options sidebar. */
  onEditOptions?: () => void
}) {
  const [text, setText] = useState(initialText)
  const [active, setActive] = useState(-1)
  const [rect, setRect] = useState<DOMRect | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const done = useRef(false)
  const { panelRef, size, startResize, resetSize } = useResizableSize(null, LIST_SIZE_OPTIONS)

  useLayoutEffect(() => {
    const input = inputRef.current
    if (!input) return
    input.focus()
    input.setSelectionRange(input.value.length, input.value.length)
    // Measured again when the visible area changes: on phones the editor sits in a bar that
    // moves up when the keyboard opens.
    const measure = () => setRect(input.getBoundingClientRect())
    measure()
    window.visualViewport?.addEventListener('resize', measure)
    window.addEventListener('resize', measure)
    return () => {
      window.visualViewport?.removeEventListener('resize', measure)
      window.removeEventListener('resize', measure)
    }
  }, [])

  const matches = useMemo(() => {
    const needle = text.trim().toLocaleLowerCase()
    if (needle === '') return options.slice(0, MAX_SUGGESTIONS)
    // The exact value first, then values starting with the text, then those
    // merely containing it.
    const exact: string[] = []
    const starts: string[] = []
    const contains: string[] = []
    for (const option of options) {
      const lower = option.toLocaleLowerCase()
      if (lower === needle) exact.push(option)
      else if (lower.startsWith(needle)) starts.push(option)
      else if (lower.includes(needle)) contains.push(option)
    }
    return [...exact, ...starts, ...contains].slice(0, MAX_SUGGESTIONS)
  }, [options, text])

  const finish = (action: () => void) => {
    if (done.current) return
    done.current = true
    action()
  }
  const chosen = () => (active >= 0 && matches[active] !== undefined ? matches[active]! : text)

  return <>
    <input
      ref={inputRef}
      value={text}
      onChange={(event) => { setText(event.target.value); setActive(-1) }}
      onClick={(event) => event.stopPropagation()}
      onBlur={() => finish(() => onConfirm(text))}
      onKeyDown={(event) => {
        if (event.key === 'ArrowDown' && matches.length > 0) {
          event.preventDefault()
          setActive((index) => Math.min(index + 1, matches.length - 1))
        } else if (event.key === 'ArrowUp' && matches.length > 0) {
          event.preventDefault()
          setActive((index) => Math.max(index - 1, -1))
        } else if (event.key === 'Enter') {
          event.preventDefault()
          // Nothing highlighted: Enter takes the top suggestion for what was typed.
          const value = active < 0 && text.trim() !== '' && matches[0] !== undefined ? matches[0] : chosen()
          finish(() => onNavigate(1, 0, value))
        } else if (event.key === 'Tab') {
          event.preventDefault()
          const value = chosen()
          finish(() => onNavigate(0, event.shiftKey ? -1 : 1, value))
        } else if (event.key === 'Escape') {
          event.preventDefault()
          finish(onCancel)
        }
      }}
      className={className}
      role="combobox"
      aria-expanded={matches.length > 0}
      aria-autocomplete="list"
    />
    {rect && (matches.length > 0 || onEditOptions) && (() => {
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight
      const above = viewportHeight - rect.bottom < 160 && rect.top > 160
      return createPortal(
      <div
        ref={panelRef}
        className="fixed z-[1000] flex flex-col overflow-hidden rounded-lg border border-border bg-surface text-sm shadow-lg"
        // Above the input when the space below is short, as it is over a phone keyboard.
        style={{
          left: rect.left,
          // Tall enough for every suggestion, up to the edge of the window on the side it opens.
          maxHeight: size ? undefined : (above ? rect.top : viewportHeight - rect.bottom) - 8,
          // As wide as the longest suggestion until resized, never past the window's edge.
          minWidth: size ? undefined : 140,
          width: size?.width ?? 'max-content',
          maxWidth: window.innerWidth - rect.left - 8,
          height: size?.height,
          ...(above
            ? { bottom: window.innerHeight - rect.top + 2 }
            : { top: rect.bottom + 2 }),
        }}
      >
      <ul role="listbox" className={`min-h-0 flex-1 overflow-auto p-1`}>
        {matches.map((option, index) => (
          <li
            key={option}
            role="option"
            aria-selected={index === active}
            // mousedown, so the input's blur doesn't commit the typed text first.
            onMouseDown={(event) => {
              event.preventDefault()
              finish(() => onConfirm(option))
            }}
            onMouseEnter={() => setActive(index)}
            className={`cursor-pointer rounded px-2 py-0.5 text-fg ${index === active ? 'bg-surface-3' : ''}`}
          >{option}</li>
        ))}
      </ul>
      <EditOptionsFooter
        onEditOptions={onEditOptions && (() => finish(() => { onCancel(); onEditOptions() }))}
        onResize={startResize}
        onResetSize={resetSize}
      />
      </div>,
      document.body,
    )
    })()}
  </>
}
